import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import {
  Wrench, CheckCircle2, ArrowRight, Shield, Clock, Euro,
  AlertTriangle, Cog, Replace, Phone, MapPin, Hammer
} from 'lucide-react';

const HERO_IMG = 'https://images.unsplash.com/photo-1562259929-b4e1fd3aef09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHx0ZWNobmljaWFuJTIwcmVwYWlyaW5nJTIwd2luZG93JTIwZG9vciUyMHRvb2xzJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc5MjA1Mjk3M3ww&ixlib=rb-4.1.0&q=85';

const INTERVENTION_TYPES = [
  { icon: Cog, title: 'Riparazione Meccanismi', desc: 'Cerniere rotte, maniglie difettose, chiusure che non tengono: ripariamo ogni componente dei tuoi serramenti.' },
  { icon: Replace, title: 'Sostituzione Componenti', desc: 'Guarnizioni consumate, vetri danneggiati, parti usurate: sostituiamo con ricambi di qualità.' },
  { icon: AlertTriangle, title: 'Problemi di Chiusura', desc: 'Finestre che non chiudono bene, spifferi, infiltrazioni: risolviamo ogni problema di tenuta.' },
  { icon: Hammer, title: 'Regolazione e Messa a Punto', desc: 'Serramenti disallineati, difficoltà di apertura: li rimettiamo in perfetto funzionamento.' },
];

const PRICING_ITEMS = [
  { label: 'Costo chiamata', value: 'EUR 25', icon: Phone },
  { label: 'Manodopera (min. 1 ora)', value: 'EUR 40/h', icon: Clock },
  { label: 'Rimborso kilometrico', value: 'In base alla sede', icon: MapPin },
];

const WHY_US = [
  'Oltre 40 anni di esperienza su serramenti in alluminio, PVC e legno',
  'Tecnico specializzato direttamente a casa tua',
  'Nessun costo nascosto: sai prima quanto spendi',
  'Paghi solo a lavoro finito, direttamente al tecnico',
  'Ricambi originali e materiali di qualità',
];

export default function InterventoInfo() {
  const navigate = useNavigate();

  useEffect(() => {
    const fonte = new URLSearchParams(window.location.search).get('fonte');
    if (fonte) localStorage.setItem('regola_fonte', fonte);
  }, []);

  const handlePrenota = () => {
    navigate('/preventivo');
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/logo-regola.png" alt="REGOLA" className="h-8" />
          </div>
          <Button onClick={handlePrenota} className="bg-amber-500 hover:bg-amber-600 text-white rounded-full text-sm font-semibold gap-2" data-testid="header-cta">
            Richiedi Intervento <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-1.5 rounded-full text-xs font-bold mb-6">
                <Wrench className="w-3.5 h-3.5" />
                SERVIZIO SU RICHIESTA
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight mb-5" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Intervento a<br />
                <span className="text-amber-500">Chiamata</span>
              </h1>
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-8 max-w-lg">
                Hai un problema con una finestra, una porta o una tapparella?
                Il nostro tecnico specializzato interviene direttamente a casa tua per <strong>riparare, sostituire o sistemare</strong> i tuoi serramenti.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={handlePrenota} size="lg" className="bg-amber-500 hover:bg-amber-600 text-white rounded-full text-base font-semibold gap-2 px-8" data-testid="hero-cta">
                  Richiedi Intervento <ArrowRight className="w-5 h-5" />
                </Button>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Euro className="w-4 h-4" />
                  <span>Paghi solo a lavoro finito</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img src={HERO_IMG} alt="Tecnico al lavoro" className="w-full h-[340px] object-cover" />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-900">Tecnici esperti</p>
                  <p className="text-[11px] text-slate-500">Specializzati dal 1984</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tipi di intervento */}
      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-lg font-bold text-slate-900 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Quando Richiedere un Intervento
            </h2>
            <p className="text-sm text-slate-500 max-w-lg mx-auto">
              Il nostro servizio copre qualsiasi problema sui tuoi serramenti. Ecco le situazioni più comuni.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {INTERVENTION_TYPES.map((s, i) => (
              <Card key={i} className="border-0 shadow-sm rounded-xl hover:shadow-md transition-all hover:-translate-y-0.5" data-testid={`intervention-type-${i}`}>
                <CardContent className="p-6">
                  <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center mb-4">
                    <s.icon className="w-5 h-5 text-amber-500" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-900 mb-2">{s.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{s.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing + Perché noi */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Pricing */}
            <Card className="border-0 shadow-md rounded-2xl overflow-hidden">
              <div className="bg-amber-500 px-6 py-4">
                <h3 className="text-white font-bold text-base" style={{ fontFamily: "'Manrope', sans-serif" }}>Costi Trasparenti</h3>
                <p className="text-amber-100 text-xs mt-1">Nessuna sorpresa, sai prima quanto spendi</p>
              </div>
              <CardContent className="p-6 space-y-4">
                {PRICING_ITEMS.map((p, i) => (
                  <div key={i} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
                        <p.icon className="w-4 h-4 text-amber-500" />
                      </div>
                      <span className="text-sm text-slate-700">{p.label}</span>
                    </div>
                    <span className="text-sm font-bold text-slate-900">{p.value}</span>
                  </div>
                ))}
                <p className="text-xs text-slate-400 pt-2">
                  Il costo finale dipende dalla durata dell'intervento e dai materiali necessari. Il tecnico valuterà sul posto e ti comunicherà il preventivo prima di procedere.
                </p>
              </CardContent>
            </Card>

            {/* Why us */}
            <div className="bg-slate-900 rounded-2xl p-8">
              <h3 className="text-base font-bold text-white mb-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Perché Scegliere REGOLA
              </h3>
              <ul className="space-y-4">
                {WHY_US.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-300">{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-6 border-t border-slate-700">
                <div className="grid grid-cols-2 gap-3">
                  {['Brescia', 'Verona', 'Mantova', 'Cremona'].map((p) => (
                    <div key={p} className="flex items-center gap-2 bg-slate-800 rounded-lg px-4 py-3">
                      <MapPin className="w-4 h-4 text-amber-400" />
                      <span className="text-sm text-white font-medium">{p}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Finale */}
      <section className="bg-white py-16">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-lg font-bold text-slate-900 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Hai un problema con i serramenti?
          </h2>
          <p className="text-sm text-slate-500 mb-8">
            Descrivi il problema, scegli data e orario e il nostro tecnico verrà direttamente a casa tua. Paghi solo a lavoro finito.
          </p>
          <Button onClick={handlePrenota} size="lg" className="bg-amber-500 hover:bg-amber-600 text-white rounded-full text-base font-semibold gap-2 px-10" data-testid="final-cta">
            Richiedi un Intervento <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-xs text-slate-400">REGOLA Manutenzione Serramenti — Dal 1984 al tuo servizio</p>
        </div>
      </footer>
    </div>
  );
}
